import { useEffect, useState, useCallback, useRef, useId } from "react";
import { createPortal } from "react-dom";
import { Flame, X } from "lucide-react";
import useMixpanelEvent from "@/hooks/useMixpanelEvent";
import { useUser } from "@/context/UserContext";
import clsx from "clsx";

type DismissType = "timeout" | "tap_outside" | "x_button" | "save_clicked";

interface StreakModalProps {
    show: boolean;
    currentStreak: number;
    previousStreak: number;
    streakChangeType: "incremented" | "reset" | "already_completed" | "guest_static";
    isGuest: boolean;
    onClose: (dismissType: DismissType) => void;
    /** Persist pending guesses + start Auth0 signup (same handler as scoreboard row Save it). */
    onGuestSaveRegister: () => void;
}

const ORANGE = "#ff8a3d";
const ANIMATION_DURATION_MS = 1500;
const AUTO_DISMISS_REGISTERED_MS = 3500;
const AUTO_DISMISS_GUEST_MS = 5000;

/**
 * StreakModal displays the user's puzzle streak with an animated number roll-up.
 * Shows conditionally for registered users (streak change) and guests (once per session).
 * Auto-dismisses after a timeout and routes to the Scoreboard tab on any dismiss.
 */
export default function StreakModal({
    show,
    currentStreak,
    previousStreak,
    streakChangeType,
    isGuest,
    onClose,
    onGuestSaveRegister,
}: StreakModalProps) {
    const { sendEvent: sendMixpanelEvent } = useMixpanelEvent();
    const { userState } = useUser();

    const titleId = useId();
    const descId = useId();
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [display, setDisplay] = useState(0);
    const [settled, setSettled] = useState(false);
    const startedRef = useRef(false);
    const openTimeRef = useRef<number>(0);
    const hasTrackedShownRef = useRef<boolean>(false);
    const dismissTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const onCloseRef = useRef(onClose);
    onCloseRef.current = onClose;
    const handleDismissRef = useRef<(dismissType: DismissType) => void>(() => {});

    const handleDismiss = useCallback(
        (dismissType: DismissType) => {
            if (dismissTimerRef.current) {
                clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = null;
            }
            const timeOnModalMs = openTimeRef.current ? Date.now() - openTimeRef.current : 0;
            sendMixpanelEvent("Streak Modal Dismissed", {
                pageUrl: window.location.href,
                email: userState?.emailAddress || null,
                nickname: userState?.nickname || null,
                current_streak: currentStreak,
                is_guest: isGuest,
                dismiss_type: dismissType,
                time_on_modal_ms: timeOnModalMs,
            });
            onCloseRef.current(dismissType);
        },
        [currentStreak, isGuest, sendMixpanelEvent, userState?.emailAddress, userState?.nickname],
    );
    handleDismissRef.current = handleDismiss;

    const handleSaveClick = useCallback(() => {
        sendMixpanelEvent("Streak Save Clicked", {
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            current_streak: currentStreak,
        });
        onGuestSaveRegister();
        handleDismiss("save_clicked");
    }, [
        currentStreak,
        handleDismiss,
        onGuestSaveRegister,
        sendMixpanelEvent,
        userState?.emailAddress,
        userState?.nickname,
    ]);

    // Number animation + auto-dismiss
    useEffect(() => {
        if (!show) {
            setDisplay(0);
            setSettled(false);
            startedRef.current = false;
            return;
        }
        if (startedRef.current) return;
        startedRef.current = true;
        openTimeRef.current = Date.now();

        const target = isGuest ? 1 : currentStreak;
        const start = performance.now();
        let raf = 0;
        const tick = (now: number) => {
            const t = Math.min(1, (now - start) / ANIMATION_DURATION_MS);
            const eased = 1 - Math.pow(1 - t, 3);
            setDisplay(Math.round(eased * target));
            if (t < 1) {
                raf = requestAnimationFrame(tick);
            } else {
                setSettled(true);
            }
        };
        raf = requestAnimationFrame(tick);

        const totalMs = isGuest ? AUTO_DISMISS_GUEST_MS : AUTO_DISMISS_REGISTERED_MS;
        dismissTimerRef.current = setTimeout(() => {
            handleDismissRef.current("timeout");
        }, totalMs);

        return () => {
            cancelAnimationFrame(raf);
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
            startedRef.current = false;
        };
    }, [show, isGuest, currentStreak]); // eslint-disable-line react-hooks/exhaustive-deps

    // Track "Streak Modal Shown" once
    useEffect(() => {
        if (!show || hasTrackedShownRef.current) return;
        hasTrackedShownRef.current = true;
        sendMixpanelEvent("Streak Modal Shown", {
            pageUrl: window.location.href,
            email: userState?.emailAddress || null,
            nickname: userState?.nickname || null,
            current_streak: currentStreak,
            is_guest: isGuest,
            streak_change_type: streakChangeType,
            previous_streak: isGuest ? undefined : previousStreak,
        });
    }, [show, currentStreak, previousStreak, streakChangeType, isGuest, sendMixpanelEvent, userState?.emailAddress, userState?.nickname]);

    // Reset tracking ref when modal hides
    useEffect(() => {
        if (!show) {
            hasTrackedShownRef.current = false;
        }
    }, [show]);

    // Escape-to-close + focus management
    useEffect(() => {
        if (!show) return;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                handleDismissRef.current("x_button");
            }
        };
        document.addEventListener("keydown", handleKeyDown);
        const previouslyFocused = document.activeElement as HTMLElement | null;
        requestAnimationFrame(() => panelRef.current?.focus());
        return () => {
            document.removeEventListener("keydown", handleKeyDown);
            previouslyFocused?.focus?.();
        };
    }, [show]);

    if (!show) return null;

    const modalContent = (
        <div
            onClick={() => handleDismiss("tap_outside")}
            className="tw-fixed tw-inset-0 tw-z-[1100] tw-flex tw-items-center tw-justify-center tw-bg-[rgba(8,4,18,0.7)] tw-backdrop-blur-[2px] tw-animate-[streakFadeIn_180ms_ease-out]"
        >
            <style>{`
                @keyframes streakFadeIn { from { opacity: 0 } to { opacity: 1 } }
                @keyframes streakPop { from { transform: scale(0.92); opacity: 0 } to { transform: scale(1); opacity: 1 } }
            `}</style>

            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                aria-describedby={descId}
                tabIndex={-1}
                onClick={(e) => e.stopPropagation()}
                className="tw-relative tw-flex tw-flex-col tw-items-center tw-w-[280px] tw-pt-7 tw-px-[22px] tw-pb-[22px] tw-rounded-[20px] tw-bg-[#1a1a2e] tw-border tw-border-solid tw-border-white/[0.08] tw-shadow-[0_20px_60px_rgba(0,0,0,0.5)] tw-animate-[streakPop_220ms_ease-out] focus:tw-outline-none"
            >
                <button
                    onClick={() => handleDismiss("x_button")}
                    aria-label="Close"
                    className="tw-absolute tw-top-2.5 tw-right-2.5 tw-bg-transparent tw-border-none tw-cursor-pointer tw-p-1.5 tw-text-white/60 tw-flex"
                >
                    <X size={16} />
                </button>

                <div
                    className={clsx(
                        "tw-flex tw-items-center tw-justify-center tw-gap-2 tw-py-2.5 tw-px-[22px] tw-rounded-full tw-min-w-[110px] tw-transition-transform tw-duration-200 tw-ease-out",
                        isGuest
                            ? "tw-bg-transparent tw-border tw-border-dashed tw-border-[#ff8a3d]"
                            : "tw-bg-[#ff8a3d] tw-border-none",
                        settled ? "tw-scale-[1.04]" : "tw-scale-100",
                    )}
                >
                    <Flame size={24} color={isGuest ? ORANGE : "#fff"} fill={isGuest ? "transparent" : "#fff"} />
                    <span
                        className={clsx(
                            "tw-text-[28px] tw-font-bold tw-tabular-nums tw-leading-none tw-min-w-[22px] tw-text-center",
                            isGuest ? "tw-text-[#ff8a3d]" : "tw-text-white",
                        )}
                    >
                        {display}
                    </span>
                </div>

                <div
                    id={titleId}
                    className={clsx(
                        "tw-mt-4 tw-text-2xl tw-font-medium tw-text-center tw-leading-[1.2]",
                        isGuest ? "tw-text-[#ff8a3d]" : "tw-text-white",
                    )}
                >
                    {isGuest ? "Day 1 streak started" : `${currentStreak} day streak`}
                </div>

                <div
                    id={descId}
                    className={clsx(
                        "tw-mt-1.5 tw-text-xs tw-text-center",
                        isGuest ? "tw-text-[rgba(255,138,61,0.9)]" : "tw-text-white/80",
                    )}
                >
                    {isGuest ? "Register now to save it" : "Come back tomorrow to keep it going"}
                </div>

                {isGuest && (
                    <button
                        onClick={handleSaveClick}
                        className="tw-mt-[18px] tw-bg-[#ff8a3d] tw-border-none tw-rounded-[14px] tw-py-2 tw-px-5 tw-text-white tw-text-xs tw-font-semibold tw-cursor-pointer"
                    >
                        Save it
                    </button>
                )}
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
}
